/** The ⋯ / right-click menu of a task: copy link, snooze, reschedule, move, duplicate, archive, delete. */
import type { ReactNode } from 'react';
import {
  Archive,
  BellRing,
  CalendarDays,
  Copy,
  CopyPlus,
  FolderInput,
  Link2,
  Trash2,
} from 'lucide-react';
import { todayInTz, type Task } from '@karya/core';
import { useTimeSettings, useWorkspace } from '@/data/queries';
import * as act from '@/data/actions';
import * as m from '@/data/mutations';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
} from '@/components/ui/menu';

function useListOptions() {
  const ws = useWorkspace();
  return ws.spaces.map((s) => ({
    space: s,
    lists: ws.lists.filter((l) => l.space_id === s.id && !l.archived_at),
  }));
}

export function TaskMoreMenu({
  task,
  children,
  align = 'end',
}: {
  task: Task;
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  const groups = useListOptions();
  const { tz } = useTimeSettings();
  const today = todayInTz(tz);
  return (
    <Menu>
      <MenuTrigger asChild>{children}</MenuTrigger>
      <MenuContent align={align} className="w-56" onClick={(e) => e.stopPropagation()}>
        <MenuItem
          icon={<Link2 className="size-4" />}
          onSelect={() => void act.copyTaskLink(task.id)}
        >
          Copy link
        </MenuItem>
        <MenuSub>
          <MenuSubTrigger icon={<BellRing className="size-4" />}>Snooze</MenuSubTrigger>
          <MenuSubContent>
            <MenuItem onSelect={() => void act.snoozeWithToast(task, '10m')}>10 minutes</MenuItem>
            <MenuItem onSelect={() => void act.snoozeWithToast(task, '1h')}>1 hour</MenuItem>
            <MenuItem onSelect={() => void act.snoozeWithToast(task, 'tomorrow')}>
              Tomorrow morning
            </MenuItem>
          </MenuSubContent>
        </MenuSub>
        <MenuSub>
          <MenuSubTrigger icon={<CalendarDays className="size-4" />}>Reschedule</MenuSubTrigger>
          <MenuSubContent>
            <MenuItem
              onSelect={() => void m.updateTask(task.id, { due_date: m.quickDate('today', today) })}
            >
              Today
            </MenuItem>
            <MenuItem
              onSelect={() =>
                void m.updateTask(task.id, { due_date: m.quickDate('tomorrow', today) })
              }
            >
              Tomorrow
            </MenuItem>
            <MenuItem
              onSelect={() =>
                void m.updateTask(task.id, { due_date: m.quickDate('weekend', today) })
              }
            >
              This weekend
            </MenuItem>
            <MenuItem
              onSelect={() =>
                void m.updateTask(task.id, { due_date: m.quickDate('next_week', today) })
              }
            >
              Next week
            </MenuItem>
            <MenuItem
              onSelect={() => void m.updateTask(task.id, { due_date: null, due_time: null })}
            >
              Remove date
            </MenuItem>
          </MenuSubContent>
        </MenuSub>
        <MenuSub>
          <MenuSubTrigger icon={<FolderInput className="size-4" />}>Move to</MenuSubTrigger>
          <MenuSubContent className="w-56">
            {groups.map((g) => (
              <div key={g.space.id}>
                <MenuLabel>{g.space.name}</MenuLabel>
                {g.lists.map((l) => (
                  <MenuItem
                    key={l.id}
                    disabled={l.id === task.list_id}
                    onSelect={() => void m.moveTask(task.id, l.id)}
                  >
                    {l.emoji ? `${l.emoji} ` : ''}
                    {l.name}
                  </MenuItem>
                ))}
              </div>
            ))}
          </MenuSubContent>
        </MenuSub>
        <MenuItem
          icon={<CopyPlus className="size-4" />}
          onSelect={() => void act.duplicateWithToast(task)}
        >
          Duplicate
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          icon={<Archive className="size-4" />}
          onSelect={() => void m.archiveTask(task.id, !task.archived_at)}
        >
          {task.archived_at ? 'Unarchive' : 'Archive'}
        </MenuItem>
        <MenuItem
          icon={<Trash2 className="size-4" />}
          destructive
          onSelect={() => void act.deleteWithToast(task)}
        >
          Delete
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function TaskContextMenu({
  task,
  children,
  onOpen,
}: {
  task: Task;
  children: ReactNode;
  onOpen: () => void;
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        <ContextMenuItem onSelect={onOpen}>Open</ContextMenuItem>
        <ContextMenuItem
          icon={<Link2 className="size-4" />}
          onSelect={() => void act.copyTaskLink(task.id)}
        >
          Copy link
        </ContextMenuItem>
        <ContextMenuItem
          icon={<BellRing className="size-4" />}
          onSelect={() => void act.snoozeWithToast(task, '1h')}
        >
          Snooze 1 hour
        </ContextMenuItem>
        <ContextMenuItem
          icon={<Copy className="size-4" />}
          onSelect={() => void act.duplicateWithToast(task)}
        >
          Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          icon={<Archive className="size-4" />}
          onSelect={() => void m.archiveTask(task.id, !task.archived_at)}
        >
          {task.archived_at ? 'Unarchive' : 'Archive'}
        </ContextMenuItem>
        <ContextMenuItem
          icon={<Trash2 className="size-4" />}
          destructive
          onSelect={() => void act.deleteWithToast(task)}
        >
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
